/** @jsx jsx */
import { Global, css, jsx } from '@emotion/react';
import { createContext, useMemo } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { NonIdealState } from '@blueprintjs/core';
import {
	ConfigFetcher,
	LoginScreen,
	SlykitContext,
	useApplicationState,
	useFocusedSessionPolling,
	useSlykit,
} from 'slykit';
import { useLoadApplicationSpecificStateLocal } from './hooks/useLoadApplicationSpecificStateLocal';
import { Navigation } from './views/navigation';
import { PlatipusExplorer } from './views/explorer/PlatipusExplorer';
import logo from './images/corona-virus-pixabay-bw.png';

export const LoginContext = createContext(false);

const _404 = () => <NonIdealState icon={'zoom-out'} title={'404 Not Found'} />;

export function Root() {
	return (
		<ConfigFetcher>{({ config }) => <App config={config} />}</ConfigFetcher>
	);
}

function App({ config }) {
	const { isReady, authenticate, error } = useSlykit(
		config.hostName,
		config.path,
		{ noAnonymous: config.noAnonymous }
	);

	// initialize the application specific data
	const initialState = useLoadApplicationSpecificStateLocal();

	// set up state management
	const [state, dispatch] = useApplicationState(initialState);

	// memoize the values
	const contextValue = useMemo(() => {
		return { state, dispatch };
	}, [state, dispatch]);

	// Because this app only has explorers we only need auth then the user is focused on the page
	useFocusedSessionPolling();

	return (
		<LoginContext.Provider value={isReady}>
			<SlykitContext.Provider value={contextValue}>
				<Global
					styles={css`
						html,
						body,
						#root {
							width: 100%;
							height: 100%;
							margin: 0;
							padding: 0;
							font-family: ArialMT, sans-serif;
							overflow: hidden;
							display: flex;
							flex-flow: row nowrap;
						}
						* {
							box-sizing: border-box;
						}
						svg {
							fill: currentColor;
						}
					`}
				/>
				<BrowserRouter basename={config.publicURL || '/'}>
					{ isReady && 
						<div css={layout}>
							<Navigation />
							<Switch>
								<Route exact path="/">
									<Redirect to="/explorer" />
								</Route>
								<Route exact path="/explorer" component={PlatipusExplorer} />
								<Route component={_404} />
							</Switch>
						</div>
					}
					{ !isReady && 
						<div style={{ padding: 150, textAlign: "center", width: "100%"}}>
							<h3>Loading PLATIPUS...</h3>
						</div>
						
					}
					{ config.noAnonymous  &&
						<LoginScreen
							title={Title}
							error={error}
							isAuthenticated={isReady}
							authenticate={authenticate}
							anonymous={!config.noAnonymous}
							projectLogo={logo}
							projectLogoStyle={logoStyle}
						/>
					}
				</BrowserRouter>
			</SlykitContext.Provider>
		</LoginContext.Provider>
	);
}

const Title = () => {
	return '';
};

const layout = css`
	display: flex;
	flex-flow: column;
	flex: 1 1 auto;
`;
const logoStyle = css`
	position: absolute;
	left: 40%;
	top: 40%;
	width: 20%;
	opacity: 0.2;
`;
